import { useEffect, useState } from "react"
import _forEach from "lodash/forEach"
import _map from "lodash/map"
import _round from "lodash/round"
import Bugsnag from "@bugsnag/js"

import useGtm from "@src/hooks/use-gtm"
import { useAppDispatch, useAppSelector } from "@src/hooks/use-redux"
import useStoryblokBridge from "@src/hooks/use-storyblok-bridge"

import { GET_BUNDLE } from "@src/operations/api/product"

import getGlobalSiteSettings from "@src/pages/api/get-global-site-settings"
import getPaths from "@src/pages/api/get-bundle-paths"

import { setCartCount } from "@src/redux/cart.slice"
import { getUser } from "@src/redux/user.slice"

import apolloClient from "@src/utilities/apollo-client"
import populateCards from "@src/utilities/populate-cards"
import Storyblok from "@src/utilities/storyblok"
import getStoryblokApiKey from "@src/utilities/storyblok-api-key"

import { addProductsToCart } from "@src/workers/api/add-to-cart"
import { getBundlePriceStock } from "@src/workers/api/get-prices-and-stock"
import getStoryblokCacheVersion from "@src/workers/storyblok/get-cache-version"

import GeneralLayout from "@src/layouts/general"
import PageBuilder from "@src/components/page-builder"
import ProductHeader from "@src/components/ProductHeader"
import SEO from "@src/components/seo"

import type { GetStaticPropsContext, InferGetStaticPropsType } from "next"
import type onyxandrose from "@src/entities"
import type {
  GetBundleQuery,
  GetBundleQueryVariables,
} from "@src/entities/types/api"

export default function BundlePage({
  bundle,
  preview,
  siteSettings,
  story,
  storyblokApiKey,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const dispatch = useAppDispatch()
  const user = useAppSelector(getUser)
  const blok = useStoryblokBridge(story, storyblokApiKey, preview)

  const [addingToBag, setAddingToBag] = useState<boolean>(false)
  const [frequencyInWeeks] = useState<number>(4)
  const [isInStock, setIsInStock] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [quantity, setQuantity] = useState<number>(1)
  const [retailPrice, setRetailPrice] = useState<number>(bundle.priceRetail)
  const [salePrice, setSalePrice] = useState<number | null>(
    bundle.priceOnSale ?? null
  )
  const [selectedPurchaseType, setSelectedPurchaseType] =
    useState<onyxandrose.Types.CartItemPurchaseType>("one-time")

  const changeSelectedPurchaseType = (
    newType: onyxandrose.Types.CartItemPurchaseType
  ) => {
    setSelectedPurchaseType(newType)
  }

  const getLatestPriceStock = async (skus: string[]) => {
    const { data } = await getBundlePriceStock(skus)
    if (data) {
      setRetailPrice(data.bundle.priceRetail)
      setSalePrice(data.bundle.priceOnSale ?? null)
      setIsLoading(false)
      let hasStockForAllProductsInBundle = true
      _forEach(data.bundle.products, (product) => {
        if (product.stock === 0) {
          hasStockForAllProductsInBundle = false
        }
      })
      setIsInStock(hasStockForAllProductsInBundle)
    }
  }

  const handleAddToCart = async () => {
    setAddingToBag(true)
    const { cartCount, errors } = await addProductsToCart(
      _map(bundle.products, (product) => ({
        quantity,
        sku: product.sku,
      }))
    )
    if (errors) {
      _forEach(errors, (error) => {
        Bugsnag.notify(error)
      })
      setAddingToBag(false)
      return
    }
    dispatch(setCartCount(cartCount))
    setAddingToBag(false)
  }

  const onChangeQuantity = (newQuantity: number) => {
    setQuantity(newQuantity)
  }

  useEffect(() => {
    if (bundle) {
      const skus = _map(bundle.products, (product) => product.sku)
      getLatestPriceStock(skus)
    }
  }, [bundle])

  useEffect(() => {
    if (user) {
      useGtm({
        event: "orTrack",
        eventName: "Product List Viewed",
        properties: {
          list_id: blok.content.product_name,
          products: _map(bundle.products, (product) => ({
            category: "Bundles",
            image_url: product.thumbnailUrl,
            name: product.name,
            price: _round(0.01 * product.priceRetail, 2),
            product_id: product.sku,
            quantity: 1,
            sku: product.sku,
            ...(product.description && { variant: product.description }),
            value: _round(quantity * 0.01 * bundle.priceRetail, 2),
          })),
        },
      })
    }
  }, [user])

  return (
    <GeneralLayout siteSettings={siteSettings}>
      {blok.content && blok.content.seo && <SEO {...blok.content.seo} />}
      <ProductHeader
        amazonUrl={null}
        featuredImages={
          blok.content?.featured_images?.map((image: any) => image.filename) ??
          []
        }
        inStock={isInStock}
        isAddingToBag={addingToBag}
        isReady={user.authStateKnown && !isLoading}
        minimumQuantityForSubscription={1}
        onAddToCart={handleAddToCart}
        onChangeFrequency={() => {}}
        onChangeSelectedPurchaseType={changeSelectedPurchaseType}
        onChangeQuantity={onChangeQuantity}
        originalRetailPrice={retailPrice}
        oneTimePurchasePrice={salePrice}
        productIconLinks={blok?.content?.product_links}
        productLongDescription={Storyblok().richTextResolver.render(
          blok.content.product_long_description
        )}
        productName={blok.content.product_name}
        productPageUrl={blok.full_slug}
        productShortDescription={blok.content.product_short_description}
        selectedFrequencyInWeeks={frequencyInWeeks}
        selectedQuantity={quantity}
        selectedPurchaseType={selectedPurchaseType}
        subscriptionPurchasePrice={null}
        wholesalePurchasePrice={null}
      />
      {blok &&
        blok.content &&
        blok.content.page_builder &&
        blok.content.page_builder.map((body: any) => (
          <PageBuilder content={body} key={body._uid} />
        ))}
    </GeneralLayout>
  )
}

export const getStaticPaths = async () => {
  const paths = await getPaths()
  return {
    paths: _map(paths, (slug) => ({
      params: {
        slug,
      },
    })),
    fallback: false,
  }
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const slug = (context.params && (context.params.slug as string)) ?? ""
  const apiKey = getStoryblokApiKey(context.preview)
  const cacheVersion = await getStoryblokCacheVersion()

  const [siteSettings, storyRequest] = await Promise.all([
    getGlobalSiteSettings(context.preview ?? false),
    Storyblok(apiKey).get(`cdn/stories/bundle/${slug}`, {
      cv: cacheVersion,
      version: context.preview ? "draft" : "published",
    }),
  ])

  const skus: string[] =
    storyRequest?.data?.story?.content?.bundled_products_sanity?.skus ?? []

  const bundle = await apolloClient
    .query<GetBundleQuery, GetBundleQueryVariables>({
      query: GET_BUNDLE,
      variables: { skus: skus ?? [] },
    })
    .then(({ data }) => data.bundle)

  const story = await populateCards(storyRequest.data.story)

  return {
    props: {
      bundle,
      preview: context.preview ?? false,
      siteSettings,
      story,
      ...(context.preview && { storyblokApiKey: apiKey }),
    },
  }
}
